import React from 'react'
import PropTypes from "prop-types";
import {getFormInputCssClass, getInputErrorClass, hasMessage, setupOnExtractData} from "../../../util/FormUtil";
import UserDataService from "../../common/UserDataService";
import {fetchResponseHandler} from "../../../util/FetchUtil";
import {isNotBlank} from "../../../util/StringUtil";

export const PROP_NAME_PHONE_COUNTRY_CODE = "phoneCountryCode";
export const PROP_NAME_PHONE_NUMBER = "phoneNumber";
export const PROP_NAME_PHONE_EXTENSION = "phoneExtension";
export const PROP_NAME_PHONE_IS_MOBILE = "phoneIsMobile";
export const PROP_NAME_PHONE_2FA_CHANNEL = "phone2FAChannel";
export const PROP_NAME_ENABLE_2FA = "enable2fa";

export const CHANNEL_SMS = "SMS";
export const CHANNEL_VOICE = "VOICE";
const phonePatternFailMessage = "Only number, space, dash, period, and parentheses are allowed in a phone number";
const phoneExtensionPatternFailMessage = "The primary phone number extension must be numeric";
const phonePremiumFailMessage = "The primary phone number you have entered is considered a premium-rate number and will not be allowed";

class PhoneInputComponent extends React.Component {
    constructor(props) {
        super(props);
        this.phoneCountryCodeChange = this.phoneCountryCodeChange.bind(this);
        this.phoneNumberChange = this.phoneNumberChange.bind(this);
        this.phoneExtensionChange = this.phoneExtensionChange.bind(this);
        this.phoneIsMobileChange = this.phoneIsMobileChange.bind(this);
        this.phone2FAChannelChange = this.phone2FAChannelChange.bind(this);
        this.checkPremiumPhone = this.checkPremiumPhone.bind(this);
        this.extractData = this.extractData.bind(this);
        this.enable2FAChange = this.enable2FAChange.bind(this);
        this.cleanseSetPhoneNumber = this.cleanseSetPhoneNumber.bind(this);
    }

    componentDidMount() {
        const {setFormState, userDataService, metadata, data, required, smsOnly, userDataInit} = this.props;
        if(!metadata[PROP_NAME_PHONE_COUNTRY_CODE]) {
            //Init country code to 1, not dirty, and valid
            setFormState({property: PROP_NAME_PHONE_COUNTRY_CODE, data: data[PROP_NAME_PHONE_COUNTRY_CODE] ? data[PROP_NAME_PHONE_COUNTRY_CODE] : "1", metadata: {dirty: false, valid: true}});
        }
        if(!metadata[PROP_NAME_PHONE_NUMBER]) {
            //Init phone number to current data or empty, not dirty, and valid if populated or not required
            setFormState({property: PROP_NAME_PHONE_NUMBER, data: data[PROP_NAME_PHONE_NUMBER], metadata: {dirty: false, valid: required ? !!data[PROP_NAME_PHONE_NUMBER] : true}});
        }
        if(!metadata[PROP_NAME_PHONE_EXTENSION]) {
            //Init extension to current data or empty, not dirty, and valid
            setFormState({property: PROP_NAME_PHONE_EXTENSION, data: data[PROP_NAME_PHONE_EXTENSION], metadata:{dirty: false, valid: true}});
        }
        if(!metadata[PROP_NAME_PHONE_IS_MOBILE]) {
            //Init isMobile to current data or false, not dirty, and valid
            setFormState({property: PROP_NAME_PHONE_IS_MOBILE, data: data[PROP_NAME_PHONE_IS_MOBILE] ? data[PROP_NAME_PHONE_IS_MOBILE] : false, metadata:{dirty: false, valid: true}});
        }
        if(!metadata[PROP_NAME_PHONE_2FA_CHANNEL]) {
            //Init 2fa to current data or SMS if SMSOnly, VOICE if not. Not dirty and valid.
            setFormState({property: PROP_NAME_PHONE_2FA_CHANNEL, data: data[PROP_NAME_PHONE_2FA_CHANNEL] ? data[PROP_NAME_PHONE_2FA_CHANNEL] : (smsOnly ? CHANNEL_SMS : CHANNEL_VOICE), metadata: {dirty: false, valid: true}});
        }
        if(!metadata[PROP_NAME_ENABLE_2FA]) {
            //Init enable2fa to current data or empty (false), not dirty and valid
            setFormState({property: PROP_NAME_ENABLE_2FA, data: data[PROP_NAME_ENABLE_2FA], metadata:{dirty: false, valid: true}});
        }

        //Save property if phone was empty on the way in, also run ipstack
        if(!data[PROP_NAME_PHONE_NUMBER] && userDataService){
            this.phoneBlankOnInit = true;



            userDataService.getIPStack()
                .then(fetchResponseHandler)
                .then(
                    function successCallback(data) {
                        let calling_code = data.location.calling_code;
                        if(calling_code && calling_code.includes(",")){
                            calling_code = calling_code.split(",")[0];
                        }
                        let containsPhoneCountryCode = userDataInit[PROP_NAME_PHONE_NUMBER].phoneCountryCodes.find(element => element.code === calling_code);
                        if(!containsPhoneCountryCode){
                            calling_code = userDataInit[PROP_NAME_PHONE_NUMBER].phoneCountryCodes[0].code;
                        }
                        setFormState({property: PROP_NAME_PHONE_COUNTRY_CODE, data: calling_code, metadata: {dirty: true, valid: true}});
                    },
                    function errorCallback(data) {
                        console.log("Failed to fetch data from IPStack.");
                    });
        }

        setupOnExtractData(this);
    }

    extractData(){
        const {data, smsOnly} = this.props;
        if (isNotBlank(data[PROP_NAME_PHONE_NUMBER])){
            const result = {};
            result[PROP_NAME_PHONE_COUNTRY_CODE] = data[PROP_NAME_PHONE_COUNTRY_CODE];
            result[PROP_NAME_PHONE_NUMBER] = data[PROP_NAME_PHONE_NUMBER];
            result.twoFactorChannel = data[PROP_NAME_PHONE_2FA_CHANNEL];
            result[PROP_NAME_PHONE_IS_MOBILE] = data[PROP_NAME_PHONE_IS_MOBILE];
            if (! smsOnly && ! data[PROP_NAME_PHONE_IS_MOBILE] && data[PROP_NAME_PHONE_2FA_CHANNEL] === CHANNEL_VOICE) {
                result[PROP_NAME_PHONE_EXTENSION] = data[PROP_NAME_PHONE_EXTENSION];
            }
            return result;
        }
        return null;
    }

    /*
        Auto-correct the phone number
        This additional validation is applicable for the United States - only
     */
    cleanseSetPhoneNumber(countryCode, phoneNumberValue) {
        const {onError, setFormState} = this.props;

        if (phoneNumberValue && phoneNumberValue.trim().length > 0 && phoneNumberValue.startsWith('1') &&
            countryCode === '1') {
            if (onError) {
                onError("Removed the country code prefix '1' from the phone number, as it is already defined.", "warning");
            }
            phoneNumberValue = phoneNumberValue.substring(1);
        }

        setFormState({property: PROP_NAME_PHONE_NUMBER, data: phoneNumberValue});
    }

    phoneCountryCodeChange(e){
        const {data, setFormState, metadata} = this.props;
        const countryCodeValue = e.target.value;
        const phoneNumber = data[PROP_NAME_PHONE_NUMBER];

        this.checkPremiumPhone(countryCodeValue, phoneNumber, metadata[PROP_NAME_PHONE_NUMBER]);

        setFormState({property: PROP_NAME_PHONE_COUNTRY_CODE, data: countryCodeValue,
            metadata:{dirty: true, valid: true}});

        this.cleanseSetPhoneNumber(countryCodeValue, phoneNumber);
    }

    phoneNumberChange(e){
        const {data, setFormState, userDataInit, metadata, required} = this.props;
        let isValid = false;
        let isDirty = false;
        let message = metadata[PROP_NAME_PHONE_NUMBER] ? metadata[PROP_NAME_PHONE_NUMBER].message : "";

        //If phone number value exists and isnt 0 length
        const phoneNumberPrefixValue = data[PROP_NAME_PHONE_COUNTRY_CODE];
        const phoneNumberValue = e.target.value;
        if(phoneNumberValue && phoneNumberValue.trim().length > 0){
            //Dirty since nonzero length
            isDirty = true;
            //Test the pattern for validity
            if (userDataInit[PROP_NAME_PHONE_NUMBER].phonePattern.test(phoneNumberValue)) {
                isValid = true;
            }else{
                message = phonePatternFailMessage;
            }
        }
        //If doesnt exist or zero length
        else{
            //If we werent empty on the way in, this means that being blank indicates deletion, therefore dirty
            if(!this.phoneBlankOnInit){
                isDirty = true;
            }
            //If we are blank, then we are only valid if not required
            isValid = !required;
        }

        //If we change phone number and SMS is checked, then enable2fa needs to be checked
        if(!data[PROP_NAME_ENABLE_2FA] && data[PROP_NAME_PHONE_2FA_CHANNEL] == CHANNEL_SMS){
            setFormState({property: PROP_NAME_ENABLE_2FA, metadata:{valid: false}})
        }

        const newPhoneNumberMetadata = {dirty: isDirty, valid: isValid, message: message};
        this.cleanseSetPhoneNumber(phoneNumberPrefixValue, phoneNumberValue);
        this.checkPremiumPhone(data[PROP_NAME_PHONE_COUNTRY_CODE], phoneNumberValue, newPhoneNumberMetadata);
    }

    phoneExtensionChange(e){
        const {setFormState, userDataInit} = this.props;
        let isValid = false;
        let isDirty = false;
        let message = null;

        //If phone extension value exists and isnt 0 length
        if(e.target.value && e.target.value.trim().length > 0){
            //Dirty since nonzero length
            isDirty = true;
            //Test the pattern for validity
            if (userDataInit[PROP_NAME_PHONE_EXTENSION].phoneExtensionPattern.test(e.target.value)) {
                isValid = true;
            }else{
                message = phoneExtensionPatternFailMessage;
            }
        }
        setFormState({property: PROP_NAME_PHONE_EXTENSION, data: e.target.value, metadata:{dirty: isDirty, valid: isValid, message: message}})    }

    phoneIsMobileChange(e){
        const {setFormState, data} = this.props;

        //If they are switching to not mobile, we have to force the user to select call since we will disable SMS option.
        if(e.target.checked === false){
            setFormState({property: PROP_NAME_PHONE_2FA_CHANNEL, data: CHANNEL_VOICE, metadata: {dirty: true, valid: true}});

            //If using voice, enable2fa is valid no matter the value
            setFormState({property: PROP_NAME_ENABLE_2FA, metadata:{valid: true}})

        }
        //If it's a mobile number, clear and then hide the extension field
        else{
            setFormState({property: PROP_NAME_PHONE_EXTENSION, data: "", metadata: {dirty: false, valid: true}});

            //If we switch to mobile, then enable2fa needs to be checked
            if(!data[PROP_NAME_ENABLE_2FA]){
                setFormState({property: PROP_NAME_ENABLE_2FA, metadata:{valid: false}})
            }
        }

        setFormState({property: PROP_NAME_PHONE_IS_MOBILE, data: e.target.checked, metadata:{dirty: true, valid: true}})    }

    phone2FAChannelChange(e){
        const {setFormState, data} = this.props;
        setFormState({property: PROP_NAME_PHONE_2FA_CHANNEL, data: e.target.value, metadata: {dirty: true, valid: true}});

        //If we switch to mobile, then enable2fa needs to be checked
        if(e.target.value === CHANNEL_SMS){
            setFormState({property: PROP_NAME_ENABLE_2FA, metadata:{valid: data[PROP_NAME_ENABLE_2FA]}})
        }
        //Switching to VOICE, therefore 2fa agreement not required, mark as valid
        else{
            setFormState({property: PROP_NAME_ENABLE_2FA, metadata:{valid: true}})
        }
    }

    enable2FAChange(e){
        const {setFormState} = this.props;
        setFormState({property: PROP_NAME_ENABLE_2FA, data: e.target.checked, metadata: {dirty: true, valid: e.target.checked}})
    }

    checkPremiumPhone(countryCode, phoneNumber, newPhoneNumberMetadata){
        const {userDataInit, userDataService, setFormState, metadata} = this.props;
        userDataService.checkPremiumPhone({countryCode:countryCode, phoneNumber:phoneNumber})
            .then(fetchResponseHandler)
            .then(resp => {
                if(resp.truth){
                    setFormState({property: PROP_NAME_PHONE_NUMBER,
                        metadata:{...newPhoneNumberMetadata, valid:false, message: phonePremiumFailMessage}})
                }else{
                    setFormState({property: PROP_NAME_PHONE_NUMBER,
                        metadata:{...newPhoneNumberMetadata}})
                }
            })
    }

    render() {
        const {data, disableVoiceOption = false, metadata, userDataInit, required, smsOnly, countryCodeWidth} = this.props;
        const requiredClassName = required ? "" : " optional";
        const hasInputClassPhoneNumber = getFormInputCssClass(metadata, PROP_NAME_PHONE_NUMBER);
        const hasInputClassPhoneExtension = getFormInputCssClass(metadata, PROP_NAME_PHONE_EXTENSION);
        // width percentage of 30 hides Country description on My Profile, 20 for Enhanced Security
        const countryCodeWidthCss = (countryCodeWidth) ? "max-width--" + countryCodeWidth : "max-width--30";

        return (
            <div className={"grid form-fields"}>
                <div className={"grid__col-sm-5" + requiredClassName}>
                    <div className={" hard--top hard--sides soft-half--bottom"}>
                        <label htmlFor={"phoneNumber"}>Primary Phone Number</label>
                        <div style={{whiteSpace: 'nowrap', display: 'flex'}}>
                            <select id="phoneCountryCode"
                                    className={"select-input--large " + countryCodeWidthCss}
                                    value={data[PROP_NAME_PHONE_COUNTRY_CODE]}
                                    onChange={this.phoneCountryCodeChange}
                            >
                                {
                                    userDataInit[PROP_NAME_PHONE_NUMBER].phoneCountryCodes
                                        .map(c =>
                                            (<option value={c.code}
                                                     dangerouslySetInnerHTML={{__html: c.htmlDescWithCode}}></option>)
                                        )
                                }
                            </select>
                            <input id={"phoneNumber"}
                                   className={"text-input text-input--large responsive" + hasInputClassPhoneNumber}
                                   value={data[PROP_NAME_PHONE_NUMBER]}
                                   maxLength={userDataInit[PROP_NAME_PHONE_NUMBER].maxLengthPhone}
                                   onChange={this.phoneNumberChange}
                            />
                        </div>
                        {
                            hasMessage(metadata, PROP_NAME_PHONE_NUMBER) &&
                            <div className="error"
                                 style={{display: 'block'}}>{metadata[PROP_NAME_PHONE_NUMBER].message}</div>
                        }
                    </div>
                </div>
                {
                    ! smsOnly && ! data[PROP_NAME_PHONE_IS_MOBILE] &&
                    <div className={"grid__col-sm-2"}>
                        {
                            <div className={"optional hard--top hard--sides soft-half--bottom"}>
                                <label htmlFor={"phoneExtension"}>Extension</label>
                                <input id={"phoneExtension"}
                                       className={"text-input text-input--large responsive " + hasInputClassPhoneExtension}
                                       value={data[PROP_NAME_PHONE_EXTENSION]}
                                       maxLength={userDataInit[PROP_NAME_PHONE_NUMBER].maxLengthPhoneExtension}
                                       onChange={this.phoneExtensionChange}
                                />
                                {
                                    hasMessage(metadata, PROP_NAME_PHONE_EXTENSION) &&
                                    <div
                                        className="error display--block">{metadata[PROP_NAME_PHONE_EXTENSION].message}</div>
                                }
                            </div>
                        }
                    </div>
                }
                <div className={"grid__col-sm-5"}>
                    {
                        !smsOnly &&
                        <div className={"toggle push--top"}>
                            <input type={"checkbox"}
                                   id={"phoneIsMobile"}
                                   onChange={this.phoneIsMobileChange}
                                   checked={data[PROP_NAME_PHONE_IS_MOBILE]}
                            />
                            <label htmlFor={"phoneIsMobile"}
                                   style={{top: '40%'}}
                                   className={"toggle__label toggle__label-after"}
                            >
                                <label>Is this a mobile number?</label>
                            </label>
                        </div>
                    }
                </div>
                <div className="grid__col-sm-3">
                    <label>Send security code by:</label>
                    <label htmlFor={"sms2fa"}>
                        <input type={"radio"}
                               style={{verticalAlign: 'middle', marginRight: '10px'}}
                               name={PROP_NAME_PHONE_2FA_CHANNEL}
                               disabled={data[PROP_NAME_PHONE_IS_MOBILE] === false}
                               id={"sms2fa"}
                               onChange={this.phone2FAChannelChange}
                               value={CHANNEL_SMS}
                               checked={data[PROP_NAME_PHONE_2FA_CHANNEL] === CHANNEL_SMS}
                        />
                        SMS
                    </label>
                </div>
                <div className="grid__col-sm-3">
                    <label> </label>
                    {
                        //If SMSOnly, dont display the voice option
                        !smsOnly &&
                        <label style={{marginTop: '18px'}} htmlFor={"call2fa"}>
                            <input type={"radio"}
                                   style={{verticalAlign: 'middle', marginRight: '10px'}}
                                   name={PROP_NAME_PHONE_2FA_CHANNEL}
                                   id={"call2fa"}
                                   onChange={this.phone2FAChannelChange}
                                   value={CHANNEL_VOICE}
                                   checked={data[PROP_NAME_PHONE_2FA_CHANNEL] === CHANNEL_VOICE}
                                   disabled={disableVoiceOption}
                            />
                            Call
                        </label>
                    }
                </div>
                {
                    //Display the agreement when phone number is not blank and
                    data[PROP_NAME_PHONE_IS_MOBILE] &&
                    <div className="grid__col-sm-12 panel--block panel--block--border">
                        <p className={"paragraph--small"}>
                            By submitting your contact information, you acknowledge that CME Group or its parent,
                            subsidiary or affiliated companies or one of their authorized representatives may send you
                            verification codes via text message.  Such contact may use automated technology.
                            Standard text message and data rates may apply.
                        </p>
                        <p className={"paragraph--small"}>
                            You can change your preferences at any time by updating your profile or by calling us:
                        </p>
                        <p className={"grid"}>
                            <div className={"hard grid__col-4 paragraph--small"}>
                                United States: +1 312 456 1560
                            </div>
                            <div className={"hard grid__col-4 paragraph--small"}>
                                Europe: +44 20 3379 3802
                            </div>
                            <div className={"hard grid__col-4 paragraph--small"}>
                                Asia: +65 6593 5536
                            </div>
                        </p>
                        <div className={"display--flex push--top"}>
                            <input
                                type={"checkbox"}
                                name={PROP_NAME_ENABLE_2FA}
                                id={PROP_NAME_ENABLE_2FA}
                                checked={data[PROP_NAME_ENABLE_2FA]}
                                onChange={this.enable2FAChange}
                            />
                            <label htmlFor={PROP_NAME_ENABLE_2FA} style={{marginLeft: '5px'}}>I agree</label>
                        </div>
                    </div>
                }
            </div>
        );
    }
}

PhoneInputComponent.propTypes = {
    countryCodeWidth: PropTypes.string,
    // This is the object that contains properties like country, address, etc
    data: PropTypes.object.isRequired,
    disableVoiceOption: PropTypes.bool,
    // This object contains properties like valid state and error messages
    metadata: PropTypes.object.isRequired,
    // This is the function to set properties of the "data" object. The function should take property name as first parameter and the value as the second parameter.
    setFormState: PropTypes.func.isRequired,
    userDataInit: PropTypes.object.isRequired,
    userDataService: PropTypes.instanceOf(UserDataService).isRequired,
    onExtractData: PropTypes.func.isRequired,
    required: PropTypes.bool,
    smsOnly: PropTypes.bool,
    onError: PropTypes.func.isRequired,
};

export default PhoneInputComponent;

